// SafeImage (010 T069; 009 revision 5): an image from an address the API returns (publisher images, signed media,
// avatars). An image that fails to load is removed and the fallback is shown instead, so the browser's broken-image
// icon never appears over a card's designed background (009 revision 5: the Daily Dose cards showed it). A failure
// that happened before React attached its handler (a cached error) is caught when the element mounts. Images decode
// off the main thread; lists pass `loading="lazy"` for images below the fold.
import { useCallback, useState, type CSSProperties, type ReactNode } from 'react';

/** Props of {@link SafeImage}. */
export interface SafeImageProps {
  /** The image address. */
  src: string;
  /** The text alternative; empty (the default) for a decorative image. */
  alt?: string;
  className?: string;
  style?: CSSProperties;
  /** What to show when the image does not load (nothing by default: the container's own background shows). */
  fallback?: ReactNode;
  /** `lazy` for images that start below the fold (lists, grids); the browser's default otherwise. */
  loading?: 'lazy' | 'eager';
  /** Called once when the image fails (a parent that lays out around the image can then change its layout). */
  onFail?(): void;
}

/**
 * An image that gives way to its fallback when it does not load.
 * @param props the address, text alternative, classes, fallback, loading hint and failure callback
 * @returns the image, or the fallback once the image has failed
 */
export function SafeImage({
  src,
  alt = '',
  className,
  style,
  fallback = null,
  loading,
  onFail,
}: SafeImageProps) {
  const [failed, setFailed] = useState<string | null>(null);
  const fail = useCallback(() => {
    setFailed(src);
    onFail?.();
  }, [src, onFail]);
  const mounted = useCallback(
    (img: HTMLImageElement | null) => {
      if (img?.complete && img.naturalWidth === 0) fail();
    },
    [fail],
  );
  if (failed === src) return <>{fallback}</>;
  return (
    <img
      ref={mounted}
      src={src}
      alt={alt}
      className={className}
      style={style}
      loading={loading}
      decoding="async"
      onError={fail}
    />
  );
}
